import { faComment } from '@fortawesome/free-solid-svg-icons';
import { type JSX, useState } from 'react';

import { SharedTPRMApi } from 'Api/TPRM/TPRMApi';
import { Button } from 'Components/Buttons/Buttons';
import { Text } from 'Components/Text/Text';
import { iso8601ToUsTimestampLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getUserNameFromSubject } from 'Helpers/UserUtils';
import { DDQDiscussionDraft, DDQDiscussionType, QuestionnaireMetadataStatus, SubmittedDiscussionClientResponse, SubmittedDiscussionVendorResponse, ThirdPartyService } from 'Models/TPRM';
import { UserResponse } from 'Models/User';

import styles from './DiscussionTab.module.css';
import { DDQDiscussionModal } from '../DDQDiscussionModal/DDQDiscussionModal';

export interface DiscussionTabBaseProps {
    ddqApi: SharedTPRMApi;
    discussionDrafts: DDQDiscussionDraft[];
    onDDQDiscussionDraftUpdated: () => Promise<void>;
    onDDQDiscussionSubmitted: () => Promise<void>;
    setTprmAccessDenied: (value: boolean) => void;
    service: ThirdPartyService;
    allowDiscussionDrafts: boolean;
    dueDiligenceStatus?: QuestionnaireMetadataStatus;
    clientName: string;
}

interface DiscussionTabClientProps extends DiscussionTabBaseProps {
    isThirdParty: false;
    submittedDiscussions?: SubmittedDiscussionClientResponse[];
    users: UserResponse[];
}

interface DiscussionTabThirdPartyProps extends DiscussionTabBaseProps {
    isThirdParty: true;
    submittedDiscussions?: SubmittedDiscussionVendorResponse[];
}

// Make sure to only pass 'true' or 'false' literals to isThirdParty, not a general boolean variable.

export type DiscussionTabProps = DiscussionTabClientProps | DiscussionTabThirdPartyProps;

export const DiscussionTab = (props: DiscussionTabProps): JSX.Element => {
    const [isDiscussionModalOpen, setIsDiscussionModalOpen] = useState<boolean>(false);

    return (
        <>
            {isDiscussionModalOpen && props.isThirdParty === false && <DDQDiscussionModal ddqApi={props.ddqApi} discussionDrafts={props.discussionDrafts} hideModal={() => setIsDiscussionModalOpen(false)} onDDQDiscussionDraftUpdated={props.onDDQDiscussionDraftUpdated} setTprmAccessDenied={props.setTprmAccessDenied} service={props.service} isThirdParty={props.isThirdParty} onDDQDiscussionSubmitted={props.onDDQDiscussionSubmitted} users={props.users} />}
            {isDiscussionModalOpen && props.isThirdParty === true && <DDQDiscussionModal ddqApi={props.ddqApi} discussionDrafts={props.discussionDrafts} hideModal={() => setIsDiscussionModalOpen(false)} onDDQDiscussionDraftUpdated={props.onDDQDiscussionDraftUpdated} setTprmAccessDenied={props.setTprmAccessDenied} service={props.service} isThirdParty={props.isThirdParty} onDDQDiscussionSubmitted={props.onDDQDiscussionSubmitted} />}
            <div className={styles.discussionHeader}>
                <Text variant="Header2" noStyles>
                    Discussion History
                </Text>
                {props.allowDiscussionDrafts === true && (
                    <Button variant="secondary" onClick={() => setIsDiscussionModalOpen(true)} fontAwesomeImage={faComment} disabled={props.isThirdParty && props.dueDiligenceStatus === QuestionnaireMetadataStatus.QUESTIONNAIRE_SUBMITTED_BY_THIRD_PARTY}>
                        Manage Discussion Drafts
                    </Button>
                )}
            </div>
            <hr />
            <div className={styles.alignVertical}>
                {props.submittedDiscussions &&
                    props.submittedDiscussions
                        .sort((discussionA, discussionB) => (discussionA.submitted_timestamp > discussionB.submitted_timestamp ? -1 : 1))
                        .map((submittedDiscussion, index) => {
                            return (
                                <div key={index} className={styles.alignVertical}>
                                    <Text variant="Header3">{`${iso8601ToUsTimestampLong(submittedDiscussion.submitted_timestamp)}`}</Text>
                                    {submittedDiscussion.discussions.map((discussion, index) => {
                                        return (
                                            <div className={styles.selectBox} key={index}>
                                                <div className={styles.ownerCommentHistoryContainer}>
                                                    {/* If a comment is from a third party, the requested_by field will be the vendors email. If a vendor is logged in and the comment is from a client, the requested_by field will be the user ID. If a comment is from a client and a vendor is logged in, the requested_by field will be empty. */}
                                                    <div className={styles.ownerCommentHistoryUserAndTimestamp}>{props.isThirdParty ? <Text variant="Header4">{discussion._type === DDQDiscussionType.THIRD_PARTY && discussion.requested_by ? discussion.requested_by : props.clientName}</Text> : <Text variant="Header4">{discussion._type === DDQDiscussionType.CLIENT && discussion.requested_by !== undefined ? getUserNameFromSubject(discussion.requested_by, props.users) : discussion.requested_by}</Text>}</div>
                                                    <Text>{discussion.comment}</Text>
                                                </div>
                                                <hr />
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
            </div>
        </>
    );
};
